<template>
  <div>
    <SelectedEmailAndAssignment 
      :emailData="currentEmail"
      :assignment="selectedAssignment"
      :initiallyCollapsed="false"
      @assignmentChanged="onAssignmentChanged"
    />
    
    <!-- Current Due Date Display -->
    <!-- <div class="date-field">
      <div class="current-due-date">
        <span class="current-due-date-text">Current Due Date: {{ currentDueDateDisplay }}</span>
      </div>
    </div> -->
    
    <!-- Delivery Date Input Field -->
    <div class="date-field">
      <DateComponent
        v-model="inputDeliveryDate"
        label="Delivery Date"
        :required="true"
        field-id="deliveryDate"
        @input="handleDateInput"
        :podDateMissing="isDeliveryDateEmpty"
      />
    </div>
    
    <div class="processing-options" v-if="config.processingOptions.markAsProcessed.visible">
      <p class="processing-options-title mt-2">Processing Options</p>
      <div class="checkbox-group">
        <v-checkbox v-model="markAsProcessed" :label="config.processingOptions.markAsProcessed.label" color="#003f2d"
          hide-details density="compact" class="custom-checkbox"></v-checkbox>
      </div>
    </div>
    <IsLoadingComponent v-if="showLoading" />
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import SelectedEmailAndAssignment from "@/components/commons/SelectedEmailAndAssignment.vue";
import DateComponent from '../commons/DateComponent.vue';
import IconProvider from '../commons/IconProvider.vue';
import processingOptions from "../../mixins/processingOptions.js";
import IsLoadingComponent from '../IsLoadingComponent.vue'
import { markEmailAsCompleteNew, sendEmail } from '../../service/Actions.js';
import { handleRecentActions } from "../../service/engagement.js";
import { ACTION_TYPES } from '../../constants.js';
import { startTiming, addProcessingTime, addProcessingTimeForError } from '../../utils/metricsHelper.js';

export default {
  name: "UploadPOD",
  mixins: [processingOptions],
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    SelectedEmailAndAssignment,
    DateComponent,
    IconProvider,
    IsLoadingComponent,
  },
  data() {
    return {
      markAsProcessed: true, // Default value for the checkbox
      showLoading: false,
      inputDeliveryDate: '', // Start with empty date for Upload POD
      deliveryDate: null, // The actual delivery date value
      isDeliveryDateEmpty: false,
    };
  },
  computed: {
    ...mapGetters({
      isLoading: "assignments/isLoading", // Use the getter for assignments
      selectedAssignment: "assignments/selectedAssignment", // Use the getter for selected assignment
      currentEmail: "email/currentEmail"
    }),
    currentDueDateDisplay() {
      if (!this.selectedAssignment || 
          !this.selectedAssignment.deadline || 
          !this.selectedAssignment.deadline.assignmentDueDate) {
        return '-';
      }
      const date = new Date(this.selectedAssignment.deadline.assignmentDueDate);
      // Format as MM/DD/YYYY in UTC
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
    },
    isButtonDisabled() {
      return this.isLoading || !this.selectedAssignment || !this.deliveryDate;
    },
    isValidForSubmit() {
      return !this.isButtonDisabled;
    },
  },
  watch: {
    isValidForSubmit: {
      handler(newValue) {
        this.$emit('isValidForSubmit', newValue);
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({ 
      convertToEmlFormData: "email/convertToEmlFormData",
      uploadFiles: "assignments/uploadFiles",
      addProofOfDeliveryItem: "assignments/addProofOfDeliveryItem",
      updateDeliveryDate: "assignments/updateDeliveryDate",
      addMetricsData: "assignments/addMetricsData",
      add: "notification/add",
    }),
    
    onAssignmentChanged(assignment) {
      // Handle assignment change - could be used for validation or updates
      console.log('Assignment changed:', assignment);
    },
    
    // Date handling methods (simplified for DateComponent)
    handleDateInput(dateValue) {
      this.inputDeliveryDate = dateValue;
      if (dateValue && this.isValidDate(dateValue)) {
        // Convert MM/DD/YYYY to ISO date for deliveryDate
        const [month, day, year] = dateValue.split('/').map(Number);
        this.deliveryDate = new Date(Date.UTC(year, month - 1, day)).toISOString();
      } else {
        this.deliveryDate = null;
      }
    },
    
    isValidDate(dateString) {
      if (!dateString) return false;
      
      // Check MM/DD/YYYY format
      const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!regex.test(dateString)) return false;
      
      // Check if it's a real date
      const [month, day, year] = dateString.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      
      return date.getFullYear() === year &&
             date.getMonth() === month - 1 &&
             date.getDate() === day;
    },

    async uploadPOD() {
      // Validate delivery date before proceeding

      if (!this.inputDeliveryDate || !this.deliveryDate) {
        this.isDeliveryDateEmpty = true;
        return;
      }
      
      // Start timing
      const startTime = startTiming();
      
      // Initialize metrics payload
      const metricsPayload = {
        entityId: this.selectedAssignment.viewEventId,
        fileId: this.selectedAssignment.code,
        actionTaken: ACTION_TYPES.UPLOAD_POD,
        status: 'Completed'
      };
      
      let finalStatus = 'Completed';
      let errorMessage = null;
      
      try {
        this.showLoading = true;
        
        const formData = await this.convertToEmlFormData();

        // Create main multipart form data
        let multiFormData = new FormData();
        
        // Add the EML file
        let document = formData.get('file');
        let documentName = document.name.replace('email_', 'UploadPOD_' + this.selectedAssignment.code + '_')

        let emlFile = new File([document], documentName, {
          type: document.type,
          lastModified: document.lastModified,
        });
        multiFormData.append('files', emlFile);

        // Add metadata
        multiFormData.append('folderName', 'Proof of Delivery');

        const config = {
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
        };

        //get folderId
        let folderId = this.getWorkFileId(this.selectedAssignment.workFileLink);

        let request = {
          folderId: folderId,
          multiPartFormData: multiFormData,
          config: config,
        };

        let boxResponse = await this.uploadFiles(request);
        if (boxResponse && boxResponse.length > 0) {
          let podRequest = [
            {
              boxId: boxResponse[0].id,
              originalDocumentName: boxResponse[0].name,
              documentSize: boxResponse[0].size,
              entityId: this.selectedAssignment.viewEventId,
            }
          ]

          let response = await this.addProofOfDeliveryItem(podRequest);
          if(response && response.length > 0) {
            console.log('added proof of delivery item successfully', response);

            //Update delivery date with user-selected date
            let deliveryDtReq = {
              entityId: this.selectedAssignment.viewEventId,
              entityIds: [this.selectedAssignment.viewEventId],
              clientName: this.selectedAssignment.client,
              fileId: this.selectedAssignment.code,
              deliveryDate: this.deliveryDate, // Use the selected delivery date
              email: this.currentEmail.loggedInUser,
              documents: [
                {
                  boxFileId: boxResponse[0].id,
                  fileName: boxResponse[0].name
                }
              ],
              deliveryDateChanged: true,
              podConfirmed: false
            }

            let updateDeliveryDateResponse = await this.updateDeliveryDate(deliveryDtReq);


            // Logic to upload the POD 
            if(this.markAsProcessed) {
              // Mark email as complete with enhanced function - banner notification
              markEmailAsCompleteNew(
                'completeNotification',
                'POD uploaded successfully',
                undefined,
                true
              );
            }

            // Show success toaster notification in UI
            this.add({
              type: 'success',
              message: 'Proof of Delivery item added successfully.',
              timeout: 5
            });
          } else {
            finalStatus = 'Error';
            errorMessage = 'Failed to add Proof of Delivery item.';
          }
        }
      } catch (error) {
        console.error('Error in uploadPOD:', error);
        finalStatus = 'Error';
        errorMessage = error.message;
        
        this.add({
          type: 'error',
          message: 'Failed to upload POD. Please try again.'
        });
      } finally {
        this.showLoading = false;
        this.closeDialog();
        
        // Single metrics call with processing time
        const finalMetricsPayload = addProcessingTime(startTime, metricsPayload, finalStatus, errorMessage);
        await this.addMetricsData(finalMetricsPayload);
      }
    },
    async saveRecentActions() {
      const payload = {
        action: ACTION_TYPES.UPLOAD_POD,
        entityIds: [this.selectedAssignment.viewEventId],
        fileId: this.selectedAssignment.code
      };
      await handleRecentActions(payload, ACTION_TYPES.UPLOAD_POD);
    },
    closeDialog() {
      this.$emit('close');
    },
    getWorkFileId (value) {
      if (!value) return null
      let arr
      if (value.includes('?')) {
        let boxArr = value.split('?')
        arr = boxArr[0].split('/')
      } else {
        arr = value.split('/')
      }
      return arr && arr.length > 0 ? arr[arr.length - 1] : null
    },
  },
  
  mounted() {
    // Initialize with empty delivery date on modal load
    this.inputDeliveryDate = '';
    this.deliveryDate = null;
    this.selectedDay = null;
    
    document.addEventListener('click', this.closeCalendarOnOutsideClick);
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.closeCalendarOnOutsideClick);
  }
};
</script>
<style scoped>
/* Date field styles (matching ChangeDueDate component) */
.date-field {
  margin-bottom: 20px;
}

.current-due-date {
  margin-bottom: 16px;
}

.current-due-date-text {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.floating-input-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

/* Processing options styles */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.custom-checkbox {
  font-family: Calibre-R-Medium;
  font-style: Regular;
  vertical-align: middle;
  font-size: 14px;
}

.custom-checkbox :deep(label) {
  font-family: Calibre-R;
  font-size: 12px;
  margin-left: 8px;
}

.processing-options-title {
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  vertical-align: middle;
  color: #000;
}

.processing-options {
  padding: 8px 0;
  border-radius: 6px;
}

/* Comprehensive Responsive Design for UploadPOD */

/* Extra Small (Mobile Portrait - 320px and below) */
@media (max-width: 320px) {
  .processing-options {
    padding: 4px;
    margin: 4px 0;
  }
  
  .processing-options-title {
    font-size: 11px;
    margin-bottom: 4px;
  }
  
  .checkbox-group {
    gap: 4px;
    margin-top: 4px;
  }
  
  .custom-checkbox {
    font-size: 11px;
  }
  
  .custom-checkbox :deep(label) {
    font-size: 10px;
    margin-left: 6px;
    line-height: 1.2;
  }
  
  .custom-checkbox :deep(.v-input__control) {
    min-height: 24px;
  }
  
  .selected-assignment {
    margin-bottom: 8px;
  }
}

/* Small (Mobile Landscape - 321px to 480px) */
@media (min-width: 321px) and (max-width: 480px) {
  .processing-options {
    padding: 6px;
    margin: 6px 0;
  }
  
  .processing-options-title {
    font-size: 12px;
    margin-bottom: 6px;
  }
  
  .checkbox-group {
    gap: 6px;
    margin-top: 6px;
  }
  
  .custom-checkbox {
    font-size: 12px;
  }
  
  .custom-checkbox :deep(label) {
    font-size: 11px;
    margin-left: 7px;
    line-height: 1.3;
  }
  
  .custom-checkbox :deep(.v-input__control) {
    min-height: 28px;
  }
  
  .selected-assignment {
    margin-bottom: 10px;
  }
}

/* Medium (Tablet Portrait - 481px to 768px) */
@media (min-width: 481px) and (max-width: 768px) {
  .processing-options {
    padding: 8px;
    margin: 8px 0;
  }
  
  .processing-options-title {
    font-size: 13px;
    margin-bottom: 8px;
  }
  
  .checkbox-group {
    gap: 7px;
    margin-top: 7px;
  }
  
  .custom-checkbox {
    font-size: 13px;
  }
  
  .custom-checkbox :deep(label) {
    font-size: 12px;
    margin-left: 8px;
    line-height: 1.4;
  }
  
  .custom-checkbox :deep(.v-input__control) {
    min-height: 32px;
  }
  
  .selected-assignment {
    margin-bottom: 12px;
  }
}

/* Large (Tablet Landscape & Small Desktop - 769px to 1024px) */
@media (min-width: 769px) and (max-width: 1024px) {
  .processing-options {
    padding: 10px;
    margin: 10px 0;
  }
  
  .processing-options-title {
    font-size: 14px;
    margin-bottom: 10px;
  }
  
  .checkbox-group {
    gap: 8px;
    margin-top: 8px;
  }
  
  .custom-checkbox {
    font-size: 14px;
  }
  
  .custom-checkbox :deep(label) {
    font-size: 13px;
    margin-left: 9px;
    line-height: 1.5;
  }
  
  .custom-checkbox :deep(.v-input__control) {
    min-height: 36px;
  }
  
  .selected-assignment {
    margin-bottom: 14px;
  }
}

/* Extra Large (Desktop - 1025px and above) */
@media (min-width: 1025px) {
  .processing-options {
    padding: 12px;
    margin: 12px 0;
  }
  
  .processing-options-title {
    font-size: 15px;
    margin-bottom: 12px;
  }
  
  .checkbox-group {
    gap: 10px;
    margin-top: 10px;
  }
  
  .custom-checkbox {
    font-size: 15px;
  }
  
  .custom-checkbox :deep(label) {
    font-size: 14px;
    margin-left: 10px;
    line-height: 1.6;
  }
  
  .custom-checkbox :deep(.v-input__control) {
    min-height: 40px;
  }
  
  .selected-assignment {
    margin-bottom: 16px;
  }
}

/* High DPI / Retina Display Adjustments */
@media only screen and (-webkit-min-device-pixel-ratio: 2),
       only screen and (min-resolution: 192dpi),
       only screen and (min-resolution: 2dppx) {
  .custom-checkbox :deep(label),
  .processing-options-title {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* Touch-friendly adjustments for mobile */
@media (hover: none) and (pointer: coarse) {
  .custom-checkbox :deep(.v-input__control) {
    min-height: 44px; /* Touch target size */
  }
  
  .custom-checkbox :deep(.v-selection-control__wrapper) {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>
